import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { ArrowRight, Clock } from "lucide-react";
import { blogs } from "@/data/blogData";

export default function Blog() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.15 });

  return (
    <section
      id="blog"
      data-testid="blog-section"
      ref={ref}
      className="relative py-24 md:py-32 px-6"
      style={{ background: "var(--color-white)" }}
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-14"
        >
          <p
            className="font-body italic tracking-[0.3em] text-xs uppercase mb-4"
            style={{ color: "var(--color-accent)" }}
          >
            — Insights & Guidance —
          </p>
          <h2
            className="font-display font-bold"
            style={{
              fontSize: "clamp(2rem, 5vw, 3.25rem)",
              color: "var(--color-text-dark)",
            }}
            data-testid="blog-title"
          >
            OUR BLOG
          </h2>
          <div
            className="w-20 h-[3px] mx-auto mt-6 rounded-full"
            style={{ background: "var(--color-accent)" }}
          />
          <p
            className="font-body text-base md:text-lg mt-6 max-w-2xl mx-auto leading-relaxed"
            style={{ color: "var(--color-text-light)" }}
          >
            Thoughtful articles on mental wellness, child development, and
            practical strategies for a healthier mind.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {blogs.map((blog, i) => (
            <motion.article
              key={blog.id}
              initial={{ opacity: 0, y: 40 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className="group flex flex-col rounded-2xl overflow-hidden bg-white transition-all duration-300"
              style={{
                border: "1px solid #e3efe9",
                boxShadow: "0 10px 28px -18px rgba(31,51,41,0.15)",
              }}
              data-testid={`blog-card-${i}`}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "var(--color-accent)";
                e.currentTarget.style.boxShadow =
                  "0 20px 40px -18px rgba(107,158,140,0.35)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#e3efe9";
                e.currentTarget.style.boxShadow =
                  "0 10px 28px -18px rgba(31,51,41,0.15)";
              }}
            >
              <div
                className="h-3"
                style={{
                  background:
                    blog.category === "Child Psychology"
                      ? "linear-gradient(90deg, var(--color-primary), var(--color-accent))"
                      : "linear-gradient(90deg, var(--color-accent), var(--color-primary))",
                }}
              />

              <div className="p-7 flex flex-col flex-1">
                <div className="flex items-center justify-between mb-4">
                  <span
                    className="inline-block font-body text-xs uppercase tracking-widest px-3 py-1 rounded-full"
                    style={{
                      background: "var(--color-secondary)",
                      color: "var(--color-accent)",
                    }}
                  >
                    {blog.category}
                  </span>
                  <span className="text-2xl" aria-hidden="true">
                    {blog.icon}
                  </span>
                </div>

                <h3
                  className="font-display font-bold text-lg md:text-xl mb-3 leading-snug"
                  style={{ color: "var(--color-text-dark)" }}
                >
                  {blog.title}
                </h3>

                <p
                  className="font-body text-sm leading-relaxed mb-5 flex-1"
                  style={{ color: "var(--color-text-light)" }}
                >
                  {blog.excerpt}
                </p>

                <div
                  className="flex items-center gap-2 mb-5 font-body text-xs"
                  style={{ color: "var(--color-text-light)" }}
                >
                  <Clock size={13} />
                  <span>{blog.date}</span>
                  <span>·</span>
                  <span>{blog.readTime}</span>
                </div>

                <Link
                  to={`/blog/${blog.slug}`}
                  className="inline-flex items-center gap-2 font-display text-sm uppercase tracking-widest font-semibold transition-colors group-hover:gap-3"
                  style={{ color: "var(--color-accent)" }}
                  data-testid={`blog-read-more-${i}`}
                >
                  Read More
                  <ArrowRight size={16} />
                </Link>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
